import { useState, type ReactElement } from 'react';
import { Box, Stack, Typography, alpha, Skeleton, CircularProgress } from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useGetPropertyByIdQuery } from '../../../store/api/propertiesApi';
import { useGetCaptacionQuery } from '../../../store/api/captacionesApi';

const golden = P.brand.golden;

/**
 * Galería descargable de las fotos de una propiedad, renderizada inline en el chat
 * de Gustavo cuando la respuesta trae el link `/properties/CODE/fotos` (lo emite el
 * tool send_property_photos en el canal CRM). Cada foto se puede descargar; también
 * hay "Descargar todas". El caso de uso (Andreina): bajar las fotos para subirlas
 * al Marketplace desde otro equipo.
 *
 * La misma galería sirve para una CAPTACIÓN (`/captaciones/ID/fotos`): las fotos del
 * aviso original viven en `images[]` y el agente las necesita antes de que exista la
 * ficha de la propiedad (Karen Fernández Noda, 20/08/2026).
 */

/** Descarga cross-origin: intenta blob→archivo; si CORS lo bloquea, abre la imagen para guardar a mano. */
async function downloadImage(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(obj);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** Galería de fotos de una PROPIEDAD, por su código (ej. IMP-2026-2161). */
export function PropertyPhotosInline({ code }: { code: string }) {
  const { data: property, isLoading } = useGetPropertyByIdQuery(code);
  const images = (property?.media?.images ?? []).map((i: { url: string; thumbnail?: string }) => ({
    url: i.url, thumbnail: i.thumbnail,
  }));
  return <PhotosGallery label={code} images={property ? images : []} isLoading={isLoading} />;
}

/** Galería de fotos del AVISO ORIGINAL de una captación, por su id de Mongo. */
export function CaptacionPhotosInline({ id }: { id: string }) {
  const { data: captacion, isLoading } = useGetCaptacionQuery(id);
  const images = (captacion?.images ?? []).map((url: string) => ({ url }));
  return (
    <PhotosGallery
      label={captacion?.address || captacion?.neighborhood || 'la captación'}
      images={images}
      isLoading={isLoading}
    />
  );
}

function PhotosGallery({ label, images, isLoading }: {
  label: string;
  images: Array<{ url: string; thumbnail?: string }>;
  isLoading: boolean;
}) {
  const [downloadingAll, setDownloadingAll] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ my: 0.75, p: 1, borderRadius: 2, border: `1px solid ${alpha(golden, 0.2)}` }}>
        <Skeleton variant="text" width={160} sx={{ mb: 0.75 }} />
        <Stack direction="row" spacing={0.75}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" width={72} height={72} />)}
        </Stack>
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box sx={{ my: 0.75, p: 1.25, borderRadius: 2, border: `1px solid ${alpha(golden, 0.2)}` }}>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
          No encontré fotos cargadas para {label}.
        </Typography>
      </Box>
    );
  }

  const baseName = label.replace(/[^A-Z0-9-]/gi, '') || 'foto';
  const nameFor = (i: number) => `${baseName}-${String(i + 1).padStart(2, '0')}.jpg`;

  const downloadAll = async () => {
    setDownloadingAll(true);
    for (let i = 0; i < images.length; i++) {
      // en serie para no disparar N descargas simultáneas que el navegador bloquea
      await downloadImage(images[i].url, nameFor(i));
    }
    setDownloadingAll(false);
  };

  return (
    <Box sx={{ my: 0.75, p: 1.25, borderRadius: 2, border: `1px solid ${alpha(golden, 0.25)}`, bgcolor: alpha(golden, 0.04) }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          <PhotoLibraryRoundedIcon sx={{ fontSize: 16, color: golden }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: golden }} noWrap>
            {images.length} foto{images.length === 1 ? '' : 's'} · {label}
          </Typography>
        </Stack>
        <Box
          component="button"
          onClick={downloadAll}
          disabled={downloadingAll}
          sx={{
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
            border: `1px solid ${alpha(golden, 0.4)}`, bgcolor: alpha(golden, 0.12), color: golden,
            borderRadius: 1, px: 0.9, py: 0.4, fontSize: 11, fontWeight: 700,
            '&:hover': { bgcolor: alpha(golden, 0.2) },
            '&:disabled': { opacity: 0.6, cursor: 'default' },
          }}
        >
          {downloadingAll
            ? <CircularProgress size={12} sx={{ color: golden }} />
            : <DownloadRoundedIcon sx={{ fontSize: 14 }} />}
          Descargar todas
        </Box>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 0.75 }}>
        {images.map((img, i) => (
          <Box key={img.url ?? i} sx={{
            position: 'relative', aspectRatio: '1', borderRadius: 1.5, overflow: 'hidden',
            border: `1px solid ${alpha(golden, 0.2)}`,
            '&:hover .photo-dl': { opacity: 1 },
          }}>
            <Box
              component="img"
              src={img.thumbnail || img.url}
              alt={`${label} ${i + 1}`}
              loading="lazy"
              onClick={() => window.open(img.url, '_blank', 'noopener')}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
            />
            <Box
              className="photo-dl"
              onClick={(e) => { e.stopPropagation(); downloadImage(img.url, nameFor(i)); }}
              sx={{
                position: 'absolute', bottom: 3, right: 3, opacity: { xs: 1, md: 0 },
                transition: 'opacity .18s ease', cursor: 'pointer',
                width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center',
                bgcolor: alpha('#000', 0.6), color: '#fff',
                '&:hover': { bgcolor: golden, color: '#0A0A0A' },
              }}
            >
              <DownloadRoundedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * ÚNICA definición de qué link es una galería de fotos. Vive acá porque el chat del CRM
 * (ChatPanel) y GustavoMarkdown resuelven los links internos por separado, cada uno con
 * su copia de los regex: `/properties/<code>/fotos` estaba solo en GustavoMarkdown —que
 * usa el wallboard de soporte— así que en el chat de verdad el link caía en la card de
 * propiedad y la galería no se veía nunca. Un solo lugar, los dos la llaman.
 *
 * Devuelve el nodo a renderizar en lugar del link, o null si no es un link de fotos.
 * Se llama ANTES que los matches genéricos: `/properties/<code>/fotos` también matchea
 * el de propiedad.
 */
export function galeriaDeFotosPara(internalPath: string): ReactElement | null {
  const prop = /^\/properties\/([A-Z0-9-]+)\/fotos\b/i.exec(internalPath);
  if (prop) return <PropertyPhotosInline code={prop[1]} />;
  // La captación va por el deep-link real del tablero (`?captacionId=`) + `&fotos=1`:
  // `/captaciones/<id>/fotos` no existe como ruta y caía en el catch-all.
  const cap = /^\/captaciones\?captacionId=([a-f0-9]{24}).*[?&]fotos=1\b/i.exec(internalPath);
  if (cap) return <CaptacionPhotosInline id={cap[1]} />;
  return null;
}
